import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import GoldDivider from "@/components/GoldDivider";
import Reveal from "@/components/Reveal";

const leadership = [
{
  name: "Piyush Agarwal",
  role: "Founder & Director of Business Development",
  initials: "PA",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/lr2f50v5_Piyush%20Agarwal.jpeg",
  bio: [
    "Chartered Accountant and capital markets strategist with a formidable network spanning global industrialists, institutional investors, and India's foremost real estate developers.",
    "Combines the precision of financial structuring with deep, relationship-driven advisory. His approach is long-term, alignment-focused, and fundamentally non-transactional."
  ]
},
{
  name: "Jatin Mundhra",
  role: "Director, Hospitality",
  initials: "JM",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/lctnnsn3_4508beb4-90ff-4508-bda7-d60494894e41.jpg",
  bio: [
    "A political consultant turned real estate entrepreneur, Jatin brings a unique strategic lens to high-stakes acquisitions across the hospitality sector.",
    "Oversees the firm's luxury hotel portfolio and specialises in the acquisition and disposition of premium hospitality assets across India, Dubai, and the Maldives."
  ]
},
{
  name: "Akshat Dubey",
  role: "Director of Leasing & Land Acquisitions",
  initials: "AD",
  image: "https://customer-assets.emergentagent.com/job_ksg-capital-partners/artifacts/anir7ckc_Akshat%20Dubey.jpeg",
  bio: [
    "Strategic leasing and capital markets specialist with deep expertise across commercial and retail leasing transactions, as well as Land Acquisitions and Joint Venture Structuring.",
    "Leads the firm's land acquisition and JV structuring practice — advising developers and capital partners on complex land deals, JV frameworks, and development platform strategy across India."
  ]

}];


function PageHero() {
  return (
    <section
      data-testid="about-hero"
      className="relative pt-40 pb-28 px-6 sm:px-12 lg:px-24 overflow-hidden"
      style={{ background: "linear-gradient(to bottom, #040A12 0%, #081323 100%)" }}>

      <div className="max-w-5xl mx-auto">
        <span
          data-testid="about-hero-overline"
          className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-6 reveal visible">

          About KSG Realty
        </span>
        <h1
          data-testid="about-hero-heading"
          className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white tracking-tight leading-tight mb-8 reveal visible reveal-delay-2">

          Built on Relationships.<br />
          <span className="text-[#C6A75E]/80">Operated with Discretion.</span>
        </h1>
        <div className="h-px w-14 bg-[#C6A75E]/50 mb-8 reveal visible reveal-delay-3" />
        <p className="font-sans text-base font-light text-slate-400 leading-relaxed max-w-2xl reveal visible reveal-delay-4">KSG Realty is an institutional real estate advisory firm headquartered in Kolkata, operating across India's primary markets.

        </p>
      </div>
    </section>);

}

function StorySection() {
  return (
    <section data-testid="about-story" className="py-24 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
        <div>
          <Reveal>
            <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-6">Our Position</span>
          </Reveal>
          <Reveal delay={100}>
            <p className="font-serif text-2xl sm:text-3xl font-light text-white leading-relaxed mb-8">
              We operate where most firms cannot — at the true intersection of capital and real estate execution.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <p className="font-sans text-sm font-light text-slate-400 leading-loose">We are strategic advisors who understand both the language of capital and the complexities of real estate. Our mandate is to structure, align, and deploy — with precision and with patience.

            </p>
          </Reveal>
        </div>
        <div className="space-y-8">
          {[
          ["Confidentiality", "Every engagement is conducted with the highest discretion. Capital is private. So are our relationships."],
          ["Strategy First", "We engage only where we can add genuine strategic value. Every mandate is earned, not assumed."],
          ["Long-Term Alignment", "We do not optimize for the transaction. We optimize for the relationship — and the next transaction after that."]].
          map(([title, text], i) =>
          <Reveal key={title} delay={i * 120}>
              <div data-testid={`value-${i}`} className="border-l border-[#C6A75E]/25 pl-6">
                <h3 className="font-serif text-lg font-light text-[#C6A75E] mb-2">{title}</h3>
                <p className="font-sans text-sm font-light text-slate-500 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          )}
        </div>
      </div>
    </section>);

}

function LeadershipSection() {
  return (
    <section data-testid="about-leadership" className="py-24 px-6 sm:px-12 lg:px-24 max-w-7xl mx-auto">
      <Reveal>
        <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-[#C6A75E]/80 block mb-4">The Team</span>
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-16">
          Leadership
        </h2>
      </Reveal>

      <div className="space-y-12">
        {leadership.map((person, i) =>
        <Reveal key={person.name} delay={i * 150}>
            <div
            data-testid={`leader-detail-${i}`}
            className="grid grid-cols-1 md:grid-cols-4 gap-8 border border-[#C6A75E]/8 hover:border-[#C6A75E]/20 transition-colors duration-700 p-8 bg-[#081323]/60">

              {/* Avatar col */}
              <div className="flex flex-col items-start">
                <div className="w-28 h-28 border border-[#C6A75E]/30 overflow-hidden flex items-center justify-center mb-4 bg-[#0B1F3A]/40">
                  {person.image ? (
                    <img
                      src={person.image}
                      alt={person.name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <span className="font-serif text-2xl text-[#C6A75E]/50">{person.initials}</span>
                  )}
                </div>
                <h3 className="font-serif text-xl font-light text-white mb-1">{person.name}</h3>
                <p className="font-sans text-[10px] uppercase tracking-[0.15em] text-[#C6A75E]/60">{person.role}</p>
              </div>

              {/* Bio col */}
              <div className="md:col-span-3 flex flex-col justify-center gap-4">
                {person.bio.map((para, j) =>
              <p key={j} className="font-sans text-sm font-light text-slate-400 leading-loose">{para}</p>
              )}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>);

}

function AboutCTA() {
  return (
    <section className="py-24 px-6 sm:px-12 lg:px-24 border-t border-[#C6A75E]/10 bg-[#081323]/40">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-8">
        <Reveal>
          <p className="font-serif text-2xl font-light text-white">
            Ready to explore an opportunity?
          </p>
        </Reveal>
        <Reveal delay={200}>
          <Link
            to="/contact"
            data-testid="about-cta-link"
            className="flex items-center gap-3 px-8 py-4 border border-[#C6A75E]/60 text-[#C6A75E] font-sans text-[10px] uppercase tracking-[0.25em] hover:bg-[#C6A75E]/8 hover:border-[#C6A75E] transition-all duration-600 whitespace-nowrap">

            Contact Us
            <ArrowRight size={12} strokeWidth={1} />
          </Link>
        </Reveal>
      </div>
    </section>);

}

export default function About() {
  return (
    <div className="bg-[#040A12]" data-testid="about-page">
      <PageHero />
      <GoldDivider />
      <StorySection />
      <GoldDivider />
      <LeadershipSection />
      <GoldDivider />
      <AboutCTA />
    </div>);

}